import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Briefcase, GraduationCap, Heart, Plane, Building, Flag, MapPin, Search, Target, Send, Plus, AlertCircle, DollarSign, TrendingUp, Award, HelpCircle, Scale } from 'lucide-react';

const Canada = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Canada
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Practical guidance for work, study, family, visits, permanent residence, and citizenship.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We guide individuals and families through eligibility, documents, filings, and interviews for Canada. We support work permits under LMIA and International Mobility, study and post-graduation options, family sponsorship, visitor travel, permanent residence through Express Entry and provincial programs, and citizenship. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk with evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Professionals with a Canadian job offer or an intra-company transfer</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Students & Graduates</h3>
              <p className="text-gray-600 text-sm">Students and graduates planning a Post-Graduation Work Permit</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Members</h3>
              <p className="text-gray-600 text-sm">Spouses, partners, children, and parents or grandparents using approved routes</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Visitors</h3>
              <p className="text-gray-600 text-sm">Short term business visitors and tourists</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">PR Candidates</h3>
              <p className="text-gray-600 text-sm">Candidates ready to qualify for permanent residence and later citizenship</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What we handle at a glance Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we handle at a glance
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Employment & Business</h3>
              <p className="text-gray-600 text-sm mb-4">LMIA supported work permits, International Mobility Program including Intra-Company Transferees, CUSMA Professionals, Francophone mobility, and other LMIA-exempt categories</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Students & Graduates</h3>
              <p className="text-gray-600 text-sm mb-4">Study Permits, extensions, PGWP planning, and study-to-work pathways</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Family</h3>
              <p className="text-gray-600 text-sm mb-4">Spousal and common-law sponsorship, dependent children, Parents and Grandparents Program, Super Visa, and dependents of workers or students</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Visitors</h3>
              <p className="text-gray-600 text-sm mb-4">Temporary Resident Visa and eTA for eligible nationals, business visitor plans, and family visits</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Permanent Residence</h3>
              <p className="text-gray-600 text-sm mb-4">Express Entry streams (FSW, CEC, FST), Provincial Nominee Programs, and family based permanent residence</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Citizenship</h3>
              <p className="text-gray-600 text-sm mb-4">Naturalization for permanent residents who meet presence and tax filing rules</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We do not handle refugee claims or removal defense.
          </p>
        </div>
      </motion.section>

      {/* Employment and Business Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Employment and business routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">LMIA based work permit</h4>
                <p className="text-gray-600 text-sm">Employer tests the labour market where required. We plan advertising, job description accuracy, and wage level.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">International Mobility Program (LMIA-exempt)</h4>
                <ul className="text-gray-600 text-sm space-y-2">
                  <li>• <strong>Intra-Company Transferee:</strong> executives, managers, or specialized knowledge with qualifying relationship</li>
                  <li>• <strong>CUSMA or other trade agreements:</strong> listed professions, traders or investors for eligible nationals</li>
                  <li>• <strong>Francophone mobility:</strong> and other significant benefit categories where the profile fits</li>
                  <li>• <strong>Open work permits:</strong> for some spouses or for PR applicants who qualify for bridging permits</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">What we do</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Category Mapping</h4>
                <p className="text-gray-600 text-sm">Map role, wage, and location to eligible categories and NOC codes</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Evidence Building</h4>
                <p className="text-gray-600 text-sm">Build employer and employee evidence including company docs, job offers, and letters</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Filing & Processing</h4>
                <p className="text-gray-600 text-sm">Prepare portal filings, arrange biometrics and medicals, plan for port of entry or consulate issuance</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Students and Graduates Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Students and graduates
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Study Permit planning</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Statement of purpose and ties to home country</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Financial evidence in acceptable formats</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">School letters and conditions alignment</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Post-Graduation Work Permit</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Timing against program completion and designated learning institution rules</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Work authorization during the transition period where available</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Path from PGWP to permanent residence through provincial or Express Entry streams</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Family Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Family routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Spousal Sponsorship</h4>
                <p className="text-gray-600 text-sm">Spousal or common-law sponsorship inside or outside Canada</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Dependent Children</h4>
                <p className="text-gray-600 text-sm">Dependent children of citizens, permanent residents, or certain workers or students</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Parents & Grandparents</h4>
                <p className="text-gray-600 text-sm">Through PGP when available or through the Super Visa for extended visits</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Accompanying Dependents</h4>
                <p className="text-gray-600 text-sm">Accompanying dependents of workers and students</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Key elements we handle</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Sponsor Eligibility</h4>
                <p className="text-gray-600 text-sm">Sponsor eligibility and financial obligations</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Relationship Evidence</h4>
                <p className="text-gray-600 text-sm">Relationship evidence and cohabitation timelines</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Medical & Security</h4>
                <p className="text-gray-600 text-sm">Medicals, biometrics, and police certificates coordination</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Visitors Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Visitors
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">TRV and eTA</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Purpose Mapping</h4>
                <p className="text-gray-600 text-sm">Purpose mapping for tourism, family visits, and business visitor activities</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Itinerary and invitation letters in the correct format</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Evidence & Ties</h4>
                <p className="text-gray-600 text-sm">Proof of funds and ties to home country</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Permanent Residence Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Permanent residence
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Express Entry and PNP</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Points calculation and program choice across FSW, CEC, and FST</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Profile creation, document harvesting plan, and Invitation to Apply readiness</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Provincial nominee options that match your occupation, language, or job offer</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Medicals, biometrics, police certificates, and proof of funds where required</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Family based PR</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Spousal or partner sponsorship and dependent children with coordinated filings</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Citizenship Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Citizenship
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">Naturalization</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Presence Calculation</h4>
                <p className="text-gray-600 text-sm">Physical presence calculation and tax filing rules</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Tests & Requirements</h4>
                <p className="text-gray-600 text-sm">Language and knowledge requirements and how to book tests</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Application Process</h4>
                <p className="text-gray-600 text-sm">Application, interview, and oath planning</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Flag className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Post-Ceremony</h4>
                <p className="text-gray-600 text-sm">Passport steps after the ceremony</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirms the correct category, fees, timelines, and key risks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Pack & Forms</h3>
              <p className="text-gray-600 text-sm">Match rule text and current program guidance</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Filing & Processing</h3>
              <p className="text-gray-600 text-sm">Filing to IRCC or through VAC with biometrics, medicals, and interview preparation</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Status Rules</h3>
              <p className="text-gray-600 text-sm">Clear rules on maintaining status including bridging work permits where eligible</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Post-Approval Steps</h3>
              <p className="text-gray-600 text-sm">Social Insurance Number, provincial health coverage, and travel planning</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-end process Section */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">End-to-End Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our structured approach ensures your Canadian immigration application is handled professionally from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              {
                step: "01",
                title: "Initial Assessment",
                description: "Comprehensive evaluation of your profile, eligibility, and optimal pathway selection for Canadian immigration.",
                icon: Search
              },
              {
                step: "02", 
                title: "Strategy Development",
                description: "Customized immigration strategy with timeline, requirements, and step-by-step roadmap tailored to your situation.",
                icon: Target
              },
              {
                step: "03",
                title: "Document Preparation", 
                description: "Meticulous preparation and review of all required documents, forms, and supporting evidence for your application.",
                icon: FileText
              },
              {
                step: "04",
                title: "Application Submission",
                description: "Professional submission of your complete application with ongoing monitoring and status updates.",
                icon: Send
              },
              {
                step: "05",
                title: "Post-Submission Support",
                description: "Continued support through processing, responding to requests, and guidance on next steps after approval.",
                icon: CheckCircle
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <process.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-acBlack text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {process.step}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{process.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines Section */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Timelines & Service Levels</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent timelines and service options to match your urgency and budget requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                level: "Express",
                timeline: "2-4 weeks",
                description: "Priority handling for urgent cases with dedicated support and expedited processing.",
                features: ["Priority review", "Dedicated consultant", "Weekly updates", "Express document prep"],
                price: "Premium pricing",
                badge: "Most Popular"
              },
              {
                level: "Standard",
                timeline: "4-8 weeks", 
                description: "Comprehensive service with regular updates and professional document preparation.",
                features: ["Regular updates", "Professional prep", "Quality review", "Standard support"],
                price: "Standard rates"
              },
              {
                level: "Economy",
                timeline: "8-12 weeks",
                description: "Cost-effective option with essential services and periodic progress updates.",
                features: ["Essential services", "Periodic updates", "Document review", "Email support"],
                price: "Budget-friendly"
              },
              {
                level: "Consultation",
                timeline: "1-2 hours",
                description: "Expert advice session to understand your options and develop an immigration strategy.",
                features: ["Expert assessment", "Strategy discussion", "Pathway analysis", "Next steps plan"],
                price: "Hourly rate"
              }
            ].map((service, index) => (
              <div key={index} className="relative bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                {service.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {service.badge}
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-acBlack mb-2 font-inter">{service.level}</h3>
                  <div className="text-2xl font-black text-blue-600 mb-2">{service.timeline}</div>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-900 mb-3">{service.price}</div>
                  <button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist Section */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Documents Checklist</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Essential documentation requirements for Canadian immigration applications.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-acBlack mb-6 font-inter flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                Essential Documents
              </h3>
              <ul className="space-y-3">
                {[
                  "Valid passport and travel documents",
                  "Educational credentials and transcripts", 
                  "Language test results (IELTS/CELPIP/TEF)",
                  "Work experience letters and references",
                  "Police clearance certificates",
                  "Medical examination results",
                  "Proof of funds and financial statements",
                  "Birth certificates and family documents"
                ].map((doc, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-acBlack mb-6 font-inter flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <Plus className="w-4 h-4 text-blue-600" />
                </div>
                Supporting Documents
              </h3>
              <ul className="space-y-3">
                {[
                  "Employment contracts and job offers",
                  "Provincial nomination certificates (if applicable)",
                  "Spouse/partner documentation",
                  "Children's documents and custody papers",
                  "Property ownership documents",
                  "Investment and business records",
                  "Professional licenses and certifications",
                  "Additional relationship evidence"
                ].map((doc, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-acBlack mb-2">Document Requirements Vary</h4>
                <p className="text-gray-700 text-sm">
                  Specific document requirements depend on your immigration program, country of origin, and individual circumstances. 
                  We provide a personalized checklist during our initial consultation to ensure you have everything needed for your application.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Special Situations Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Special situations and how we handle them
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Maintained Status and Gaps</h3>
              <p className="text-gray-600 text-sm">We plan extensions or changes of status so you remain authorized</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Express Entry Score Just Short</h3>
              <p className="text-gray-600 text-sm">We model language improvements, ECA timing, job offer points, provincial options, or spouse optimizations</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Work Permit at the Border</h3>
              <p className="text-gray-600 text-sm">We confirm if port of entry issuance is available and safe for your case</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Previous Refusals</h3>
              <p className="text-gray-600 text-sm">We prepare a history summary, obtain GCMS notes where time allows, and rebuild credibility with stronger evidence</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Quebec Considerations</h3>
              <p className="text-gray-600 text-sm">We confirm provincial steps where Quebec rules apply</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Fees Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            Fees and what is not included
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Our Stage-Based Fees Include</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Strategy and eligibility</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Document pack and forms</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Submission and follow through</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-3" />
                  <span className="text-gray-600">Optional post-approval support</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Not Included</h3>
            <p className="text-gray-600 text-sm">IRCC and VAC fees, biometrics, panel physician medicals, Educational Credential Assessments, language tests, translations, courier or shipping, priority processing fees, and any lawyer fees where counsel must be engaged.</p>
          </div>
        </div>
      </motion.section>

      {/* Risks Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Risks and how we reduce them
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Wrong Category or Incorrect NOC</h3>
              <p className="text-gray-600 mb-4">Choosing wrong category or incorrect NOC code mapping.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We map duties to the correct code and confirm wage level by region.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Weak Funds or Financial Format Errors</h3>
              <p className="text-gray-600 mb-4">Insufficient or improperly formatted financial evidence.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We check ownership, history, and format against IRCC rules.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Timing Conflicts</h3>
              <p className="text-gray-600 mb-4">Conflicts with school starts or work start dates.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We plan filing order, travel windows, and maintained status or bridging work permits where eligible.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Non-Disclosure or Inconsistencies</h3>
              <p className="text-gray-600 mb-4">Failing to disclose history or providing inconsistent information.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We build a full immigration and employment or study history to keep records consistent.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border md:col-span-2">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-4">Medical or Security Issues</h3>
              <p className="text-gray-600 mb-4">Delays in obtaining medical exams or police certificates.</p>
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-700"><strong>Our mitigation:</strong> We schedule early and track validity to avoid expiries.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* After Approval Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-8">
            After approval
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Essential Services</h3>
              <p className="text-gray-600 text-sm">Social Insurance Number, banking, and provincial health coverage</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Registrations</h3>
              <p className="text-gray-600 text-sm">Address and school registrations for families</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Work Authorization</h3>
              <p className="text-gray-600 text-sm">Right to work checks for employers and proof of status for travel</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Future Planning</h3>
              <p className="text-gray-600 text-sm">Renewals and progression to permanent residence or citizenship where eligible</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What We Need Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we need to start
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Essential Information</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Updated CV or resume and any prior Canadian immigration history</li>
                  <li>• Job offer or school acceptance and employer or school contact details</li>
                  <li>• Points estimate items such as age, education, language, and experience if targeting PR</li>
                  <li>• Timeline constraints such as start date or travel plans</li>
                  <li>• Any previous refusals, arrests, or status issues</li>
                  <li>• Family details and documents for linked applications</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Next Steps</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Schedule intake and quick eligibility screen</li>
                  <li>• Receive category selection and points estimate</li>
                  <li>• Begin evidence collection and portal setup</li>
                  <li>• File with IRCC and schedule appointments</li>
                  <li>• Prepare for biometrics, medicals, and post-approval steps</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Service specific FAQs
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can I switch from visitor to worker or student inside Canada?</h3>
              <p className="text-gray-600">Sometimes yes depending on category and current public policy. We confirm eligibility and timing before you travel or file.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">How long does Express Entry take?</h3>
              <p className="text-gray-600">It depends on score, draw patterns, and document readiness. We prepare you to accept an Invitation to Apply quickly and keep medicals and police checks timed correctly.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Can my spouse work while I study?</h3>
              <p className="text-gray-600">Often yes at the post-secondary level with an open work permit for the spouse. Rules vary by program and level. We confirm your family's eligibility.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Do I need an LMIA for every job offer?</h3>
              <p className="text-gray-600">No. Many categories are LMIA-exempt under the International Mobility Program. We confirm the correct path and evidence.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">What if I studied in Canada and my PGWP is expiring?</h3>
              <p className="text-gray-600">Options include Express Entry or PNP to PR, employer supported work permits, or bridging open work permits once certain PR stages are filed. We plan the safest sequence for your situation.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Will a past refusal or overstay block me?</h3>
              <p className="text-gray-600">Not always. We review records, obtain case notes where possible, and design a disclosure and evidence strategy. Some issues can be overcome with proper planning and honest disclosure.</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section className="py-16 px-6 bg-acBlack text-white text-center" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold mb-6">
            Ready to navigate Canadian immigration?
          </h2>
          <p className="text-lg mb-8 opacity-90">
            Get practical guidance for your Canadian work, study, family, or permanent residence goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Start Your Canadian Case
            </Link>
            <Link 
              to="/contact" 
              className="border border-white hover:bg-white hover:text-acBlack px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
          <div className="flex items-center justify-center mt-8 text-sm opacity-75">
            <Phone className="w-4 h-4 mr-2" />
            <span>24-72 hour response time</span>
          </div>
        </div>
      </motion.section>

      {/* Special Situations */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Special Situations</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We handle complex cases and unique circumstances that require specialized expertise and strategic approaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Previous Refusals",
                description: "Strategic reapplication after addressing refusal reasons with strengthened evidence and improved presentation.",
                icon: Shield
              },
              {
                title: "Criminal Inadmissibility",
                description: "Rehabilitation applications, temporary resident permits, and criminal inadmissibility assessments.",
                icon: Scale
              },
              {
                title: "Medical Inadmissibility", 
                description: "Mitigation plans for excessive demand concerns and medical inadmissibility challenges.",
                icon: Heart
              },
              {
                title: "Urgent Processing",
                description: "Expedited applications for emergency situations, medical emergencies, or time-sensitive opportunities.",
                icon: Clock
              },
              {
                title: "Complex Family Cases",
                description: "Adoption cases, custody disputes, common-law relationships, and non-traditional family structures.",
                icon: Users
              },
              {
                title: "Business Immigration",
                description: "Investor programs, self-employed persons, and start-up visa applications with business plan development.",
                icon: TrendingUp
              }
            ].map((situation, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <situation.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{situation.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{situation.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Fees and Investment */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Fees & Investment</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent pricing structure with no hidden costs and flexible payment options.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-acBlack mb-6 font-inter flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                </div>
                Our Professional Fees
              </h3>
              <ul className="space-y-4">
                {[
                  { service: "Initial Consultation", fee: "$200-400", description: "Comprehensive assessment and strategy session" },
                  { service: "Express Entry", fee: "$2,500-4,500", description: "Complete application preparation and submission" },
                  { service: "Provincial Nominee Program", fee: "$3,000-5,000", description: "PNP application plus federal processing" },
                  { service: "Family Sponsorship", fee: "$2,000-3,500", description: "Sponsor and applicant documentation" },
                  { service: "Work/Study Permits", fee: "$1,500-2,500", description: "Permit applications and extensions" },
                  { service: "Business Immigration", fee: "$5,000-15,000", description: "Investor and entrepreneur programs" }
                ].map((item, index) => (
                  <li key={index} className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-acBlack">{item.service}</div>
                      <div className="text-sm text-gray-600">{item.description}</div>
                    </div>
                    <div className="font-bold text-blue-600 ml-4">{item.fee}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-acBlack mb-6 font-inter flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <Building className="w-4 h-4 text-blue-600" />
                </div>
                Government Fees
              </h3>
              <ul className="space-y-4">
                {[
                  { service: "Express Entry (Principal)", fee: "$1,365", description: "Processing and right of permanent residence fee" },
                  { service: "Express Entry (Spouse)", fee: "$1,365", description: "Processing and RPRF for accompanying spouse" },
                  { service: "Express Entry (Child)", fee: "$230", description: "Processing fee per dependent child" },
                  { service: "Work Permit", fee: "$155-255", description: "Depending on permit type and duration" },
                  { service: "Study Permit", fee: "$150", description: "Student visa processing fee" },
                  { service: "Visitor Visa", fee: "$100", description: "Temporary resident visa fee" }
                ].map((item, index) => (
                  <li key={index} className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-acBlack">{item.service}</div>
                      <div className="text-sm text-gray-600">{item.description}</div>
                    </div>
                    <div className="font-bold text-gray-700 ml-4">{item.fee}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-acBlack mb-2">Payment Terms</h4>
                <p className="text-gray-700 text-sm">
                  Professional fees are typically split: 50% retainer to begin work, 50% before submission. 
                  Government fees are paid directly to IRCC. Additional costs may include translations, medical exams, 
                  police certificates, and courier services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Risks and Mitigation */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Risks & Mitigation</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding potential challenges and our proactive strategies to minimize risks and maximize success rates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                risk: "Application Refusal",
                mitigation: "Thorough eligibility assessment, comprehensive documentation review, and strategic presentation to address potential concerns before submission.",
                probability: "Low with proper preparation"
              },
              {
                risk: "Processing Delays",
                mitigation: "Complete applications with all required documents, proactive communication with IRCC, and contingency planning for timeline variations.",
                probability: "Moderate - system dependent"
              },
              {
                risk: "Document Insufficiency",
                mitigation: "Detailed document checklist, quality review process, and backup documentation strategies to ensure completeness.",
                probability: "Very low with our process"
              },
              {
                risk: "Policy Changes",
                mitigation: "Continuous monitoring of immigration policy updates, flexible strategy adjustments, and alternative pathway identification.",
                probability: "Unpredictable but manageable"
              },
              {
                risk: "Medical/Security Issues",
                mitigation: "Early medical assessments, security clearance guidance, and mitigation strategies for potential inadmissibility concerns.",
                probability: "Case-specific assessment"
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-start mb-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-acBlack mb-1">{item.risk}</h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{item.probability}</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{item.mitigation}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* After Approval */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">After Approval</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your journey doesn't end with approval. We provide comprehensive support for your successful settlement in Canada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Landing Preparation",
                description: "Guidance on first landing requirements, documents to bring, and customs procedures for smooth entry to Canada.",
                icon: Plane,
                timeline: "Before travel"
              },
              {
                title: "Essential Services Setup",
                description: "Assistance with SIN application, health card registration, banking setup, and other essential service connections.",
                icon: Building,
                timeline: "First 30 days"
              },
              {
                title: "Settlement Support",
                description: "Resources for housing, employment, education, and community integration to help establish your new life in Canada.",
                icon: MapPin,
                timeline: "First 6 months"
              },
              {
                title: "Status Maintenance",
                description: "Ongoing guidance on maintaining status, renewal requirements, and pathways to citizenship when eligible.",
                icon: Award,
                timeline: "Ongoing"
              }
            ].map((step, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{step.title}</h3>
                <p className="text-gray-600 text-sm mb-3 leading-relaxed">{step.description}</p>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded font-medium">{step.timeline}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* What We Need to Start */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">What We Need to Start</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Basic information required for your initial consultation and case assessment.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-acBlack mb-4 font-inter">Personal Information</h3>
                  <ul className="space-y-2">
                    {[
                      "Full name and date of birth",
                      "Current country of residence",
                      "Passport details and validity",
                      "Marital status and family composition",
                      "Previous immigration history"
                    ].map((item, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-blue-600 mr-3 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-acBlack mb-4 font-inter">Background Details</h3>
                  <ul className="space-y-2">
                    {[
                      "Education level and credentials",
                      "Work experience and current employment",
                      "Language abilities (English/French)",
                      "Immigration goals and timeline",
                      "Any specific concerns or challenges"
                    ].map((item, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-blue-600 mr-3 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> This initial information helps us prepare for your consultation. 
                  Detailed documentation will be requested after we develop your immigration strategy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Common questions about Canadian immigration processes and our services.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What is the Express Entry system and how does it work?",
                answer: "Express Entry is Canada's main immigration system for skilled workers. It manages applications for Federal Skilled Worker, Canadian Experience Class, and Federal Skilled Trades programs. Candidates create an online profile, receive a Comprehensive Ranking System (CRS) score, and the highest-scoring candidates receive invitations to apply for permanent residence through regular draws."
              },
              {
                question: "How long does Canadian immigration processing take?",
                answer: "Processing times vary by program: Express Entry takes 6 months after invitation, Provincial Nominee Programs take 15-19 months total, Family Class sponsorship takes 12 months, and work permits typically take 2-12 weeks depending on the type and country of application."
              },
              {
                question: "Do I need a job offer to immigrate to Canada?",
                answer: "Not necessarily. While a job offer can significantly boost your Express Entry score, many candidates succeed without one through programs like Federal Skilled Worker or Provincial Nominee Programs that don't require job offers. However, having a valid job offer (with LMIA when required) provides additional points and pathway options."
              },
              {
                question: "What are Provincial Nominee Programs (PNPs)?",
                answer: "PNPs allow Canadian provinces and territories to nominate individuals for permanent residence based on local economic needs. Each province has different streams targeting specific occupations, education levels, or work experience. A provincial nomination provides 600 additional CRS points in Express Entry."
              },
              {
                question: "Can I include my family in my immigration application?",
                answer: "Yes, you can include your spouse/common-law partner and dependent children (under 22) in your application. They will receive the same immigration status as you. Dependent children over 22 may qualify if they're financially dependent due to physical or mental condition."
              },
              {
                question: "What happens if my application is refused?",
                answer: "If refused, you'll receive a detailed letter explaining the reasons. Depending on the grounds for refusal, you may be able to reapply immediately with additional documentation, appeal the decision (for certain programs), or address the concerns before reapplying. We analyze refusal letters and develop strategies for successful reapplication."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-acBlack mb-3 font-inter">{faq.question}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      {/* <motion.section 
        className="py-16 bg-acBlack"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-white mb-4 font-inter">Ready to Start Your Canadian Immigration Journey?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Get expert guidance and personalized strategy for your Canadian immigration goals. 
            Book your consultation today and take the first step toward your new life in Canada.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/start-claim" 
              className="bg-blue-500 hover:bg-blue-600 text-black font-bold py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center"
            >
              Start Your Application
            </Link>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-acBlack font-bold py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </motion.section> */}
    </div>
  );
};

export default Canada;
